import React, { useEffect, useState } from "react";
import {
  FiMessageSquare,
  FiPlus,
  FiTrash2,
  FiSearch,
  FiEdit2,
  FiCheck,
  FiX,
} from "react-icons/fi";
import DoctorAgentService, { Thread } from "../../services/doctorAgentService";
import DeleteThreadModal from "./DeleteThreadModal";

interface ThreadsSidebarProps {
  selectedThreadId: string | null;
  onThreadSelect: (threadId: string) => void;
  onNewThread: () => void;
  onThreadsUpdate?: () => void;
}

const ThreadsSidebar: React.FC<ThreadsSidebarProps> = ({
  selectedThreadId,
  onThreadSelect,
  onNewThread,
  onThreadsUpdate,
}) => {
  const [threads, setThreads] = useState<Thread[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [deletingThreadId, setDeletingThreadId] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [threadToDelete, setThreadToDelete] = useState<Thread | null>(null);
  const [editingThreadId, setEditingThreadId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [isUpdatingTitle, setIsUpdatingTitle] = useState(false);
  const [renamedTitles, setRenamedTitles] = useState<Record<string, string>>(
    {}
  );
  const [updatedAtOverrides, setUpdatedAtOverrides] = useState<
    Record<string, string>
  >({});

  /**
   * Fetch threads from the API
   */
  const fetchThreads = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await DoctorAgentService.getThreads();
      setThreads(data);
      // Clear local overrides after a fresh fetch; server has the truth
      setRenamedTitles({});
      setUpdatedAtOverrides({});
    } catch (err: any) {
      console.error("Failed to fetch threads:", err);
      const errorMessage =
        err?.error || err?.message || "Failed to load conversations.";
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Show delete confirmation modal
   */
  const handleDeleteClick = (thread: Thread, event: React.MouseEvent) => {
    event.stopPropagation();
    setThreadToDelete(thread);
    setShowDeleteModal(true);
  };

  /**
   * Confirm thread deletion
   */
  const handleConfirmDelete = async () => {
    if (!threadToDelete) return;

    try {
      setDeletingThreadId(threadToDelete.thread_id);
      await DoctorAgentService.deleteThread(threadToDelete.thread_id);

      // Remove from list
      setThreads((prev) =>
        prev.filter((t) => t.thread_id !== threadToDelete.thread_id)
      );

      // If deleted thread was selected, notify parent
      if (selectedThreadId === threadToDelete.thread_id) {
        onNewThread();
      }

      onThreadsUpdate?.();
      setShowDeleteModal(false);
      setThreadToDelete(null);
    } catch (err: any) {
      console.error("Failed to delete thread:", err);
      alert(err?.error || err?.message || "Failed to delete conversation.");
    } finally {
      setDeletingThreadId(null);
    }
  };

  /**
   * Cancel thread deletion
   */
  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setThreadToDelete(null);
  };

  /**
   * Start editing thread title
   */
  const handleStartEdit = (thread: Thread, event: React.MouseEvent) => {
    event.stopPropagation();
    setEditingThreadId(thread.thread_id);
    setEditTitle(thread.title);
  };

  /**
   * Cancel editing thread title
   */
  const handleCancelEdit = () => {
    setEditingThreadId(null);
    setEditTitle("");
  };

  /**
   * Save thread title
   */
  const handleSaveTitle = async () => {
    if (!editingThreadId || !editTitle.trim()) return;

    try {
      setIsUpdatingTitle(true);
      const updatedThread = await DoctorAgentService.updateThreadTitle(
        editingThreadId,
        editTitle.trim()
      );

      // Ensure immediate UI update regardless of API response payload
      const newTitle = editTitle.trim();
      setRenamedTitles((prev) => ({ ...prev, [editingThreadId]: newTitle }));
      const effectiveUpdatedAt =
        updatedThread?.updated_at || new Date().toISOString();
      setUpdatedAtOverrides((prev) => ({
        ...prev,
        [editingThreadId]: effectiveUpdatedAt,
      }));

      // Update the thread in the list
      setThreads((prev) =>
        prev.map((t) =>
          t.thread_id === editingThreadId
            ? {
                ...t,
                ...updatedThread,
                title: updatedThread?.title ?? newTitle,
                updated_at: effectiveUpdatedAt,
              }
            : t
        )
      );

      setEditingThreadId(null);
      setEditTitle("");
      onThreadsUpdate?.();
    } catch (err: any) {
      console.error("Failed to update thread title:", err);
      alert(
        err?.error || err?.message || "Failed to update conversation title."
      );
    } finally {
      setIsUpdatingTitle(false);
    }
  };

  /**
   * Handle Enter key press in edit input
   */
  const handleEditKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSaveTitle();
    } else if (e.key === "Escape") {
      handleCancelEdit();
    }
  };

  /**
   * Format date for display
   */
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  /**
   * Filter and sort threads by search query and updated_at
   */
  const filteredThreads = threads
    .filter((thread) => {
      const effectiveTitle = (
        renamedTitles[thread.thread_id] ??
        thread.title ??
        ""
      ).toLowerCase();
      const q = searchQuery.toLowerCase();
      return (
        effectiveTitle.includes(q) ||
        (thread.thread_id?.toLowerCase() || "").includes(q)
      );
    })
    .sort((a, b) => {
      // Sort by updated_at in descending order (most recent first)
      const aUpdated = updatedAtOverrides[a.thread_id] || a.updated_at;
      const bUpdated = updatedAtOverrides[b.thread_id] || b.updated_at;
      return new Date(bUpdated).getTime() - new Date(aUpdated).getTime();
    });

  useEffect(() => {
    fetchThreads();
  }, []);

  return (
    <div className="w-80 bg-white border-r border-gray-200 flex flex-col h-full shadow-sm">
      {/* Header */}
      <div className="p-5 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FiMessageSquare className="w-5 h-5 text-blue-600" />
            </div>
            Conversations
          </h2>
          <button
            onClick={onNewThread}
            className="p-2.5 text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition-all duration-200 shadow-sm hover:shadow-md"
            title="New conversation"
          >
            <FiPlus className="w-5 h-5" />
          </button>
        </div>

        {/* Search */}
        <div className="relative">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search conversations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm"
          />
        </div>
      </div>

      {/* Threads List */}
      <div className="flex-1 overflow-y-auto p-2">
        {isLoading ? (
          <div className="p-6 text-center text-gray-500">
            <div className="animate-spin w-8 h-8 border-3 border-blue-600 border-t-transparent rounded-full mx-auto mb-3" />
            <p className="text-sm font-medium">Loading conversations...</p>
          </div>
        ) : error ? (
          <div className="p-6 text-center">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-red-600 text-xl">⚠</span>
            </div>
            <p className="text-red-600 text-sm mb-3 font-medium">{error}</p>
            <button
              onClick={fetchThreads}
              className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
            >
              Retry
            </button>
          </div>
        ) : filteredThreads.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FiMessageSquare className="w-8 h-8 text-gray-400" />
            </div>
            <p className="text-sm font-medium mb-2">
              {searchQuery ? "No conversations found" : "No conversations yet"}
            </p>
            {!searchQuery && (
              <button
                onClick={onNewThread}
                className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
              >
                Start a new conversation
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {filteredThreads.map((thread) => (
              <div
                key={thread.thread_id}
                onClick={() => onThreadSelect(thread.thread_id)}
                className={`p-4 cursor-pointer transition-all duration-200 group rounded-xl border ${
                  selectedThreadId === thread.thread_id
                    ? "bg-blue-50 border-blue-200 shadow-sm"
                    : "bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300 hover:shadow-sm"
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      {editingThreadId === thread.thread_id ? (
                        <div className="flex items-center gap-2 flex-1">
                          <input
                            type="text"
                            value={editTitle}
                            onChange={(e) => setEditTitle(e.target.value)}
                            onKeyDown={handleEditKeyPress}
                            className="flex-1 px-2 py-1 text-sm border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            disabled={isUpdatingTitle}
                            autoFocus
                          />
                          <button
                            onClick={handleSaveTitle}
                            disabled={isUpdatingTitle || !editTitle.trim()}
                            className="p-1 text-green-600 hover:bg-green-50 rounded transition-colors disabled:opacity-50"
                            title="Save"
                          >
                            {isUpdatingTitle ? (
                              <div className="w-4 h-4 border-2 border-green-600 border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <FiCheck className="w-4 h-4" />
                            )}
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            disabled={isUpdatingTitle}
                            className="p-1 text-gray-500 hover:bg-gray-50 rounded transition-colors disabled:opacity-50"
                            title="Cancel"
                          >
                            <FiX className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <>
                          <h3 className="font-medium text-gray-900 text-sm truncate">
                            {thread.title || "Untitled Conversation"}
                          </h3>
                          {thread.status !== "active" && (
                            <span
                              className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                                thread.status === "completed"
                                  ? "bg-green-100 text-green-700"
                                  : "bg-gray-100 text-gray-700"
                              }`}
                            >
                              {thread.status}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full"></div>
                        {thread.message_count || 0} messages
                      </span>
                      <span>•</span>
                      <span>
                        {formatDate(
                          thread.last_message_at ||
                            thread.created_at ||
                            new Date().toISOString()
                        )}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all duration-200">
                    <button
                      onClick={(e) => handleStartEdit(thread, e)}
                      disabled={
                        editingThreadId === thread.thread_id || isUpdatingTitle
                      }
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200 disabled:opacity-50"
                      title="Edit conversation title"
                    >
                      <FiEdit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => handleDeleteClick(thread, e)}
                      disabled={
                        deletingThreadId === thread.thread_id ||
                        editingThreadId === thread.thread_id
                      }
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200 disabled:opacity-50"
                      title="Delete conversation"
                    >
                      {deletingThreadId === thread.thread_id ? (
                        <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <FiTrash2 className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Refresh button */}
      <div className="p-4 border-t border-gray-200 bg-gray-50">
        <button
          onClick={fetchThreads}
          disabled={isLoading}
          className="w-full px-4 py-2.5 text-sm font-medium text-gray-700 bg-white hover:bg-gray-100 border border-gray-200 rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md"
        >
          {isLoading ? (
            <div className="flex items-center justify-center gap-2">
              <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
              Refreshing...
            </div>
          ) : (
            "Refresh"
          )}
        </button>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteThreadModal
        isOpen={showDeleteModal}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        threadTitle={threadToDelete?.title || ""}
        isLoading={deletingThreadId === threadToDelete?.thread_id}
      />
    </div>
  );
};

export default ThreadsSidebar;
